import { useCallback, useState } from 'react';
import type { CustomFilter } from '@trinity/chat';
import { ChatCustomFilter  } from '@trinity/chat';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import * as $cf from './custom-filters';
import { CustomFilterDateFilter } from './CustomFilter.DateSelect';
import { CustomFilterMultiselect } from './CustomFilter.Multiselect';
import { useFilterSearchQuery } from './hooks/useFilterSearch';

type Props<T extends ChatCustomFilter = ChatCustomFilter> = {
  onSelect: (value: CustomFilter.Item['value']) => void;
  value: CustomFilter.Item<T>;
};

const nullFilterTypes: Set<ChatCustomFilter> = new Set([
  ChatCustomFilter.Source,
  ChatCustomFilter.AgentFilter,
  ChatCustomFilter.InteractionStartEndDate,
]);

export function CustomFilterValue(props: Props) {
  if (nullFilterTypes.has(props.value.type)) return null;
  return renderFilterValueSelect(props);
}

function renderFilterValueSelect({ onSelect, value }: Props) {
  switch (value.type) {
    case ChatCustomFilter.PracticeSetting:
      return (
        <CustomFilterMultiselect
          placeholder="Select Practice Setting"
          onSelect={onSelect}
          options={$cf.getPracticeSettings()}
          value={value.value} />
      );

    case ChatCustomFilter.InsightType:
      return <InsightTypeSelect onSelect={onSelect} value={value} />;

    case ChatCustomFilter.Product:
      return <ProductFilterSelect onSelect={onSelect} value={value} />;

    case ChatCustomFilter.TherapeuticArea:
      return <TherapeuticAreasSelect onSelect={onSelect} value={value} />;

    case ChatCustomFilter.TherapeuticSubArea:
      return <TherapeuticSubAreaFilterSelect onSelect={onSelect} value={value} />;

    case ChatCustomFilter.Sentiment:
      return (
        <CustomFilterMultiselect
          placeholder="Select Sentiment"
          isSearchable={false}
          onSelect={onSelect}
          options={$cf.getSentimentValues()}
          value={value.value} />
      );

    case ChatCustomFilter.InteractionDate:
      return <CustomFilterDateFilter onSelect={onSelect} value={value.value} />;

    case ChatCustomFilter.InterestArea:
      return <InterestAreaFilterSelect onSelect={onSelect} value={value} />;

    case ChatCustomFilter.InteractionType:
      return (
        <CustomFilterMultiselect
          placeholder="Select Interaction Type"
          onSelect={onSelect}
          options={$cf.getInteractionTypes()}
          value={value.value} />
      );

    case ChatCustomFilter.InteractionSubType:
      return <InteractionSubTypeSelect onSelect={onSelect} value={value} />;

    case ChatCustomFilter.InteractionDetail:
      return <InteractionDetailSelect onSelect={onSelect} value={value} />;

    case ChatCustomFilter.CallCenterSource:
      return <CallCenterSourceSelect onSelect={onSelect} value={value} />;

    case ChatCustomFilter.CallCenterSubSource:
      return <CallCenterSubSourceSelect onSelect={onSelect} value={value} />;

    default:
      return null;
  }
}

type TSAFilterSelectProps = {
  onSelect: (value: CustomFilter.TherapeuticSubArea['value']) => void;
  value: CustomFilter.TherapeuticSubArea;
};

const TherapeuticSubAreaFilterSelect = (props: TSAFilterSelectProps) => {

  const [input, setInput] = useState('');
  const brandIdentifier = useActiveBrandIdentifier();
  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  const query = useFilterSearchQuery({
    excluded: props.value.value.map(m => m.id),
    type: ChatCustomFilter.TherapeuticSubArea,
    value: input,
    brandIdentifier,
  });

  const loadingMessage = useCallback(() => {
    return '...Searching';
  }, []);

  return (
    <CustomFilterMultiselect
      components={{
        DropdownIndicator: null,
      }}
      placeholder="Select Therapeutic Sub Areas"
      inputValue={input}
      onInputChange={handleInputChange}
      loadingMessage={loadingMessage}
      isLoading={query.isLoading || query.isFetching}
      isSearchable={true}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

type ProductFilterSelectProps = {
  onSelect: (value: CustomFilter.Product['value']) => void;
  value: CustomFilter.Product;
};

const ProductFilterSelect = (props: ProductFilterSelectProps) => {

  const [input, setInput] = useState('');
  const brandIdentifier = useActiveBrandIdentifier();
  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  const query = useFilterSearchQuery({
    excluded: props.value.value.map(m => m.id),
    type: ChatCustomFilter.Product,
    value: input,
    brandIdentifier,
  });

  const loadingMessage = useCallback(() => {
    return '...Searching';
  }, []);

  return (
    <CustomFilterMultiselect
      components={{
        DropdownIndicator: null,
      }}
      placeholder="Select Products"
      inputValue={input}
      onInputChange={handleInputChange}
      loadingMessage={loadingMessage}
      isLoading={query.isLoading || query.isFetching}
      isSearchable={true}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

type IAFilterSelectProps = {
  onSelect: (value: CustomFilter.InterestArea['value']) => void;
  value: CustomFilter.InterestArea;
};

const InterestAreaFilterSelect = (props: IAFilterSelectProps) => {

  const [input, setInput] = useState('');
  const brandIdentifier = useActiveBrandIdentifier();
  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  const query = useFilterSearchQuery({
    excluded: props.value.value.map(m => m.id),
    type: ChatCustomFilter.InterestArea,
    value: input,
    brandIdentifier,
  });

  const loadingMessage = useCallback(() => {
    return '...Searching';
  }, []);

  return (
    <CustomFilterMultiselect
      components={{
        DropdownIndicator: null,
      }}
      placeholder="Select Interest Areas"
      inputValue={input}
      onInputChange={handleInputChange}
      loadingMessage={loadingMessage}
      isLoading={query.isLoading || query.isFetching}
      isSearchable={true}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

type TherapeuticAreasSelectProps = {
  onSelect: (value: CustomFilter.InterestArea['value']) => void;
  value: CustomFilter.TherapeuticArea;
};

const TherapeuticAreasSelect = (props: TherapeuticAreasSelectProps) => {

  const [input, setInput] = useState('');
  const brandIdentifier = useActiveBrandIdentifier();
  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  const query = useFilterSearchQuery({
    excluded: props.value.value.map(m => m.id),
    type: ChatCustomFilter.TherapeuticArea,
    value: input,
    brandIdentifier,
  });

  const loadingMessage = useCallback(() => {
    return '...Searching';
  }, []);

  return (
    <CustomFilterMultiselect
      components={{
        DropdownIndicator: null,
      }}
      placeholder="Select Therapeutic Areas"
      inputValue={input}
      onInputChange={handleInputChange}
      loadingMessage={loadingMessage}
      isLoading={query.isLoading || query.isFetching}
      isSearchable={true}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

type InteractionSubTypeProps = {
  onSelect: (value: CustomFilter.InteractionSubType['value']) => void;
  value: CustomFilter.InteractionSubType;
};

const InteractionSubTypeSelect = (props: InteractionSubTypeProps) => {

  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.InteractionSubType,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Interaction Sub Type"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

const CallCenterSourceSelect = (props: Props<ChatCustomFilter.CallCenterSource>) => {
  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.CallCenterSource,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Call Center Source"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

const CallCenterSubSourceSelect = (props: Props<ChatCustomFilter.CallCenterSubSource>) => {
  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.CallCenterSubSource,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Call Center Sub Source"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

const InteractionDetailSelect = (props: Props<ChatCustomFilter.InteractionDetail>) => {
  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.InteractionDetail,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Interaction Detail"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

const InsightTypeSelect = (props: Props<ChatCustomFilter.InsightType>) => {
  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.InsightType,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Insight Type"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};
